import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule} from '@angular/core';

import {rootRoutingConfig} from './app.routes';
import {AppComponent} from './app.component';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {KeycloakAngularModule, KeycloakService} from 'keycloak-angular';
import {initializer} from '../utils/app-init';
import {BrokerMenuComponent} from './modules/common/components/broker-menu/broker-menu.component';
import {ContributionsOverviewComponent} from './modules/common/dialogs/member-overview-dialog/contributions-overview/contributions-overview.component';
import {HomeViewComponent} from './modules/other/components/pages/home-view/home-view.component';
import {TopMenuComponent} from './modules/common/components/top-menu/top-menu.component';
import {
  PharmacyManagementViewComponent
} from './modules/pbm/components/pages/pharmacy-management-view/pharmacy-management-view.component';
import {
  AddNationalDrugViewComponent
} from './modules/pbm/components/pages/add-national-drug-view/add-national-drug-view.component';
import {
  AddMasterDrugViewComponent
} from './modules/pbm/components/pages/add-master-drug-view/add-master-drug-view.component';
import {
  AddDrugInteractionViewComponent
} from './modules/pbm/components/pages/add-drug-interaction-view/add-drug-interaction-view.component';
import {
  AddIngredientViewComponent
} from './modules/pbm/components/pages/add-ingredient-view/add-ingredient-view.component';
import {AddAtcDialogComponent} from './modules/pbm/components/dialogs/add-atc-dialog/add-atc-dialog.component';
import {
  AddNationalDrugFormularyViewComponent
} from './modules/pbm/components/pages/add-national-drug-formulary-view/add-national-drug-formulary-view.component';
import {
  AddManufacturerDialogComponent
} from './modules/pbm/components/dialogs/add-manufacturer-dialog/add-manufacturer-dialog.component';
import {
  AddDrugManagementPlanViewComponent
} from './modules/pbm/components/pages/add-drug-management-plan-view/add-drug-management-plan-view.component';
import {ErrorDialogComponent} from './modules/common/dialogs/error-dialog/error-dialog.component';
import {ConfirmationDialogComponent} from './modules/common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {AddRefillDialogComponent} from './modules/pbm/components/dialogs/add-refill-dialog/add-refill-dialog.component';
import {APP_BASE_HREF, CurrencyPipe} from '@angular/common';
import {
  AddGrandfatherDialogComponent
} from './modules/pbm/components/dialogs/add-grandfather-dialog/add-grandfather-dialog.component';
import {RulesEngineViewComponent} from './modules/other/components/pages/rules-engine-view/rules-engine-view.component';
import {
  AddRulesEngineMetadataDialogComponent
} from './modules/other/components/dialogs/add-rules-engine-metadata-dialog/add-rules-engine-metadata-dialog.component';

import {
  ClaimResultsDialogComponent
} from './modules/clm/components/dialogs/claim-results-dialog/claim-results-dialog.component';
import {
  EnterpriseManagementViewComponent
} from './modules/ent/components/pages/enterprise-management-view/enterprise-management-view.component';
import {AddProviderViewComponent} from './modules/ent/components/pages/add-provider-view/add-provider-view.component';
import {NgxMaskModule} from 'ngx-mask';
import {
  AddInsuredPersonViewComponent
} from './modules/ent/components/pages/add-insured-person-view/add-insured-person-view.component';
import {
  AddProviderPriceListViewComponent
} from './modules/ent/components/pages/add-provider-price-list-view/add-provider-price-list-view.component';
import {
  AddJustificationDialogComponent
} from './modules/clm/components/dialogs/add-justification-dialog/add-justification-dialog.component';

import {SafePipe} from './modules/common/pipes/safe.pipe';
import {
  AddPolicyholderViewComponent
} from './modules/ent/components/pages/add-policyholder-view/add-policyholder-view.component';
import {AddPolicyViewComponent} from './modules/ent/components/pages/add-policy-view/add-policy-view.component';
import {AddPriceDialogComponent} from './modules/ent/components/dialogs/add-price-dialog/add-price-dialog.component';
import {TimeElapsedFromNowPipe} from './modules/common/pipes/time-elapsed-from-now.pipe';

import {
  UpdatePaymentReferenceDialogComponent
} from './modules/clm/components/dialogs/update-payment-reference-dialog/update-payment-reference-dialog.component';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {
  ActivityHistoryDialogComponent
} from './modules/clm/components/dialogs/activity-history-dialog/activity-history-dialog.component';

import {ActivityOverrideDialogComponent, EncounterOverrideDialogComponent} from './modules/clm/components/dialogs';
// CLM Components:
import {
  ExecutePaymentViewComponent
} from './modules/clm/components/pages/execute-payment-view/execute-payment-view.component';
import {
  AddCaseFuneralViewComponent
} from './modules/clm/components/pages/add-case-funeral-view/add-case-funeral-view.component';
import {CaseMedicalComponent} from './modules/clm/components/pages/case-medical/case-medical.component';
import {CaseHospitalComponent} from './modules/clm/components/pages/case-medical/case-hospital/case-hospital.component';
import {
  CasePharmacyComponent
} from './modules/clm/components/pages/case-medical/case-pharmacy/case-pharmacy-view.component';
import {CasesComponent} from './modules/clm/components/pages/cases/cases.component';
import {
  ClaimManagementViewComponent
} from './modules/clm/components/pages/claim-management-view/claim-management-view.component';
import {PaymentsComponent} from './modules/clm/components/pages/payments/payments.component';
import {
  CaseAssociationStepComponent
} from './modules/clm/components/pages/case-shared-steps/case-association-step/case-association-step.component';
import {
  CaseTransactionStepComponent
} from './modules/clm/components/pages/case-shared-steps/case-transaction-step/case-transaction-step.component';
import {
  CaseIssuesStepComponent
} from './modules/clm/components/pages/case-shared-steps/case-issues-step/case-issues-step.component';
import {
  CaseStatusStepComponent
} from './modules/clm/components/pages/case-shared-steps/case-status-step/case-status-step.component';
import {
  CaseAdmissionStepComponent
} from './modules/clm/components/pages/case-shared-steps/medical/case-admission-step/case-admission-step.component';
import {
  CaseDocumentationStepComponent
} from './modules/clm/components/pages/case-shared-steps/medical/case-documentation-step/case-documentation-step.component';
import {
  CaseProvidersStepComponent
} from './modules/clm/components/pages/case-shared-steps/medical/case-providers-step/case-providers-step.component';
import {
  CaseDetailsStepComponent
} from './modules/clm/components/pages/case-shared-steps/medical/case-details-step/case-details-step.component';
import {
  CaseUtilisatonStepComponent
} from './modules/clm/components/pages/case-shared-steps/medical/case-utilisaton-step/case-utilisaton-step.component';
import {
  CasePaymentsStepComponent
} from './modules/clm/components/pages/case-shared-steps/case-payments-step/case-payments-step.component';
import {
  CaseDischargeDialogComponent
} from './modules/clm/components/dialogs/case-discharge-dialog/case-discharge-dialog.component';

import {
  IssueTicketManagementViewComponent
} from './modules/other/components/pages/issue-ticket-management-view/issue-ticket-management-view.component';
import {
  AddIssueTicketDialogComponent
} from './modules/clm/components/dialogs/add-issue-ticket-dialog/add-issue-ticket-dialog.component';
import {
  AddBankAccountDialogComponent
} from './modules/ent/components/dialogs/add-bank-account-dialog/add-bank-account-dialog.component';
import {
  GenericImageDialogComponent
} from './modules/common/dialogs/generic-image-dialog/generic-image-dialog.component';
import {
  ActivityResultsDialogComponent
} from './modules/clm/components/dialogs/activity-results-dialog/activity-results-dialog.component';

import {AddDocumentDialogComponent} from './modules/clm/components/dialogs/add-document-dialog/add-document-dialog.component';

// Other
import {
  MemberDataViewComponent
} from './modules/other/components/pages/data-center-management-view/member-data-view/member-data-view.component';
import {
  ExternalDataViewComponent
} from './modules/other/components/pages/data-center-management-view/external-data-view/external-data-view.component';

// ENT Components:
import {ProductsComponent} from './modules/ent/components/pages/enterprise-management-view/products/products.component';
import {BenefitsComponent} from './modules/ent/components/pages/enterprise-management-view/benefits/benefits.component';
import {LimitsComponent} from './modules/ent/components/pages/enterprise-management-view/limits/limits.component';
import {
  AddBenefitDialogComponent
} from './modules/ent/components/dialogs/add-benefit-dialog/add-benefit-dialog.component';
import {AddLimitDialogComponent} from './modules/ent/components/dialogs/add-limit-dialog/add-limit-dialog.component';
import {AddProductViewComponent} from './modules/ent/components/pages/add-product-view/add-product-view.component';
import {
  TreeBenefitsAndLimitsComponent
} from './modules/ent/components/pages/add-product-view/tree-benefits-and-limits/tree-benefits-and-limits.component';
import {
  ProviderNetworksComponent
} from './modules/ent/components/pages/enterprise-management-view/provider-networks/provider-networks.component';
import {
  AddProviderNetworkViewComponent
} from './modules/ent/components/pages/add-provider-network-view/add-provider-network-view.component';
import {
  ChangedDataErrorDialogComponent
} from './modules/clm/components/dialogs/changed-data-error-dialog/changed-data-error-dialog.component';
import {OutsideclickDirective, TooltipDirective} from './modules/common/directive';
import {
  PaymentReportDialogComponent
} from './modules/clm/components/dialogs/payment-report-dialog/payment-report-dialog.component';
import {
  PolicyProductVariancesComponent
} from './modules/ent/components/pages/add-policy-view/steps/policy-product-variances/policy-product-variances.component';
import {
  PolicyProductWaiversComponent
} from './modules/ent/components/pages/add-policy-view/steps/policy-product-waivers/policy-product-waivers.component';
import {
  PreAuthorisationPaymentDialogComponent
} from './modules/clm/components/dialogs/pre-authorisation-payment-dialog/pre-authorisation-payment-dialog.component';
import {
  EditPersonNameDialogComponent
} from './modules/ent/components/dialogs/edit-person-name-dialog/edit-person-name-dialog.component';
import {
  MemberOverviewDialogComponent
} from './modules/common/dialogs/member-overview-dialog/member-overview-dialog.component';

// MemberOverview Dialog Components
import {
  CasesOverviewComponent,
  DocumentOverviewComponent,
  EmailOverviewComponent,
  HomeAffairsPersonProfileComponent,
  MemberOverviewComponent,
  PaymentOverviewComponent,
  PolicyOverviewComponent,
  SmsOverviewComponent,
  TransactionsOverviewComponent,
  UtilisationOverviewComponent,
  VivascoreDetailsOverviewComponent,
  VivascoreProfileOverviewComponent
} from './modules/common/dialogs/member-overview-dialog';

import {AddUserDialogComponent} from './modules/other/components';
import {
  AddManualPaymentDialogComponent
} from './modules/clm/components/dialogs/add-manual-payment-dialog/add-manual-payment-dialog.component';
import {GreydomCommonModule} from './modules/common/modules/greydom-common.module';
import {ClmPartialModule} from './modules/clm/modules/clm-partial.module';
import {EntPartialModule} from './modules/clm/modules/ent-partial.module';
import {
  GenericIframeDialogComponent
} from './modules/common/dialogs/generic-iframe-dialog/generic-iframe-dialog.component';
import {LoadingSpinnerInterceptor} from './modules/common/interceptors/loading-spinner.interceptor';
import {
  MemberLookupDialogComponent
} from './modules/other/components/dialogs/member-lookup-dialog/member-lookup-dialog.component';
import {
  AddObservationDialogComponent
} from './modules/clm/components/dialogs/add-observation-dialog/add-observation-dialog.component';
import {
  DataCenterManagementViewComponent
} from './modules/other/components/pages/data-center-management-view/data-center-management-view.component';

// Pharmacy Management View Components
import {
  AtcCodesComponent,
  DrugInteractionsComponent,
  DrugManagementPlansComponent,
  ExternalDrugDataComponent,
  IngredientsComponent,
  ManufacturersComponent,
  MasterDrugsComponent,
  NationalDrugFormulariesComponent,
  NationalDrugsComponent
} from './modules/pbm/components/pages/pharmacy-management-view';
import {
  ClaimSearchCriteriaDialogComponent
} from './modules/clm/components/dialogs/claim-search-criteria-dialog/claim-search-criteria-dialog.component';
import {
  GenericReportDialogComponent
} from './modules/common/components/dialogs/generic-report-dialog/generic-report-dialog.component';
import {
  UpfrontRejectionClaimDialogComponent
} from './modules/clm/components/dialogs/upfront-rejected-claims-dialog/upfront-rejection-claim-dialog.component';
import {
  ReversalClaimDialogComponent
} from './modules/clm/components/dialogs/revertal-claim-dialog/reversal-claim-dialog.component';
import {environment} from '../environments/environment';
import {
  ManageBrokerPermissionsDialogComponent
} from './modules/common/dialogs/manage-broker-permissions-dialog/manage-broker-permissions-dialog.component';
import {GreydomPaginatorIntl} from './modules/common/services/greydom-paginator-intl.service';

// PBM Module Components
const Pbm = [
  NationalDrugsComponent,
  MasterDrugsComponent,
  DrugInteractionsComponent,
  NationalDrugFormulariesComponent,
  DrugManagementPlansComponent,
  ManufacturersComponent,
  IngredientsComponent,
  AtcCodesComponent,
  ExternalDrugDataComponent
];

// Other Module Components
const Other = [
  AddUserDialogComponent,
  DataCenterManagementViewComponent,
  MemberDataViewComponent,
  ExternalDataViewComponent
];

const dialogs = [
  ActivityOverrideDialogComponent,
  EncounterOverrideDialogComponent,
  ClaimSearchCriteriaDialogComponent,
  GenericReportDialogComponent,
  UpfrontRejectionClaimDialogComponent
];

const pipes = [
  SafePipe
];

const featMemberOverviewDialog = [
  MemberOverviewDialogComponent,
  HomeAffairsPersonProfileComponent,
  PolicyOverviewComponent,
  DocumentOverviewComponent,
  MemberOverviewComponent,
  CasesOverviewComponent,
  SmsOverviewComponent,
  EmailOverviewComponent,
  TransactionsOverviewComponent,
  UtilisationOverviewComponent,
  PaymentOverviewComponent,
  VivascoreProfileOverviewComponent,
  VivascoreDetailsOverviewComponent,
  ContributionsOverviewComponent
];

const modules = [
  GreydomCommonModule,
  ClmPartialModule,
  EntPartialModule,
];

@NgModule({
  declarations: [
    AppComponent,
    HomeViewComponent,
    TopMenuComponent,
    BrokerMenuComponent,
    PharmacyManagementViewComponent,
    AddNationalDrugViewComponent,
    AddMasterDrugViewComponent,
    AddDrugInteractionViewComponent,
    AddIngredientViewComponent,
    AddAtcDialogComponent,
    AddNationalDrugFormularyViewComponent,
    AddManufacturerDialogComponent,
    AddDrugManagementPlanViewComponent,
    ErrorDialogComponent,
    ConfirmationDialogComponent,
    AddRefillDialogComponent,
    AddGrandfatherDialogComponent,
    RulesEngineViewComponent,
    AddRulesEngineMetadataDialogComponent,
    ClaimManagementViewComponent,
    ClaimResultsDialogComponent,
    EnterpriseManagementViewComponent,
    AddProviderViewComponent,
    AddInsuredPersonViewComponent,
    SafePipe,
    AddPolicyholderViewComponent,
    AddPolicyViewComponent,
    AddProviderPriceListViewComponent,
    AddPriceDialogComponent,
    TimeElapsedFromNowPipe,
    CasesComponent,
    PaymentsComponent,
    ExecutePaymentViewComponent,
    UpdatePaymentReferenceDialogComponent,
    AddJustificationDialogComponent,
    ActivityHistoryDialogComponent,
    AddCaseFuneralViewComponent,
    AddIssueTicketDialogComponent,
    AddBankAccountDialogComponent,
    IssueTicketManagementViewComponent,
    GenericImageDialogComponent,
    GenericImageDialogComponent,
    ProductsComponent,
    BenefitsComponent,
    LimitsComponent,
    AddBenefitDialogComponent,
    AddLimitDialogComponent,
    AddProductViewComponent,
    TreeBenefitsAndLimitsComponent,
    ProviderNetworksComponent,
    AddProviderNetworkViewComponent,
    CaseMedicalComponent,
    CaseHospitalComponent,
    CasePharmacyComponent,
    CaseAssociationStepComponent,
    CaseTransactionStepComponent,
    CaseIssuesStepComponent,
    CaseStatusStepComponent,
    CaseAdmissionStepComponent,
    ChangedDataErrorDialogComponent,
    CaseDocumentationStepComponent,
    CaseProvidersStepComponent,
    CaseDetailsStepComponent,
    CaseUtilisatonStepComponent,
    CasePaymentsStepComponent,
    CaseDischargeDialogComponent,
    TooltipDirective,
    OutsideclickDirective,
    PaymentReportDialogComponent,
    PolicyProductVariancesComponent,
    PolicyProductWaiversComponent,
    PreAuthorisationPaymentDialogComponent,
    ReversalClaimDialogComponent,
    EditPersonNameDialogComponent,
    AddManualPaymentDialogComponent,
    GenericIframeDialogComponent,
    MemberLookupDialogComponent,
    AddObservationDialogComponent,
    ActivityResultsDialogComponent,
    AddDocumentDialogComponent,
    ...featMemberOverviewDialog,
    ...dialogs,
    ...Pbm,
    ...Other,
    ...pipes,
    ManageBrokerPermissionsDialogComponent,
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ],
  bootstrap: [AppComponent], imports: [BrowserModule,
    BrowserAnimationsModule,
    rootRoutingConfig,
    KeycloakAngularModule,
    NgxMaskModule.forRoot(),
    DragDropModule,
    ...modules], providers: [
    CurrencyPipe,
    TimeElapsedFromNowPipe,
    ...pipes,
    {
      provide: APP_INITIALIZER,
      useFactory: initializer,
      deps: [KeycloakService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingSpinnerInterceptor,
      multi: true,
    },
    {provide: APP_BASE_HREF, useValue: environment.contextRoute},
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {appearance: 'outline'}
    },
    {
      provide: MatPaginatorIntl,
      useClass: GreydomPaginatorIntl
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
}
